/**
 * Ist-Zahlung je Planrate (Plan 2026-10-04): Bezahlt markieren, Zahlungsdatum aendern, Zahlung
 * zuruecknehmen, Belege hochladen und verknuepfen. Gegenstueck zu
 * `backend/src/http/routes/paymentActuals.ts`; alle Wege verlangen `manage_payments`.
 *
 * Konflikte (409, `current: { raten, istZahlungen }`) kommen als `KonfliktFehler` aus `anfrage()`;
 * den Umgang damit (Toast, Nachladen) entscheidet der Aufrufer in `App.tsx`.
 */
import { anfrage, ladeHoch } from "./basis.ts";
import type { IstZahlung, OrderPayment } from "../lib/types.ts";

export interface KopfZahlstatus {
  paid: boolean;
  paidDate: string | null;
}

const bestellPfad = (orderId: string): string => `/api/orders/${encodeURIComponent(orderId)}`;
const zahlungsPfad = (orderId: string, paymentId: string): string =>
  `${bestellPfad(orderId)}/payment-actuals/${encodeURIComponent(paymentId)}`;

/** `POST .../payment-positions/:positionId/mark-paid`. `paidOn` fehlt = heute (Server). */
export function bezahltMarkieren(
  orderId: string,
  positionId: string,
  rowVersion: number,
  paidOn?: string,
): Promise<{ istZahlung: IstZahlung; rate: OrderPayment; kopf: KopfZahlstatus }> {
  return anfrage(`${bestellPfad(orderId)}/payment-positions/${encodeURIComponent(positionId)}/mark-paid`, {
    methode: "POST",
    koerper: paidOn === undefined ? { rowVersion } : { rowVersion, paidOn },
  });
}

/** `PATCH .../payment-actuals/:paymentId` - `paidOn` als `YYYY-MM-DD`. */
export function zahlungsdatumAendern(orderId: string, paymentId: string, paidOn: string): Promise<IstZahlung> {
  return anfrage<IstZahlung>(zahlungsPfad(orderId, paymentId), { methode: "PATCH", koerper: { paidOn } });
}

/** `POST .../payment-actuals/:paymentId/reverse` - der Server verlangt `bestaetigt: true` (Dialog davor). */
export function zahlungZuruecknehmen(
  orderId: string,
  paymentId: string,
  rowVersion: number,
): Promise<{ rate: OrderPayment; geloesteBelege: number; kopf: KopfZahlstatus }> {
  return anfrage(`${zahlungsPfad(orderId, paymentId)}/reverse`, {
    methode: "POST",
    koerper: { rowVersion, bestaetigt: true },
  });
}

/**
 * `POST .../payment-actuals/:paymentId/documents` (multipart, Feld `file` wie beim Bestell-Upload).
 * `fortschritt` meldet den Sendeanteil 0 bis 1 (`basis.ts#ladeHoch`).
 */
export function belegHochladen(
  orderId: string,
  paymentId: string,
  datei: File,
  fortschritt?: (anteil: number) => void,
): Promise<IstZahlung> {
  const formular = new FormData();
  formular.append("file", datei);
  return ladeHoch<IstZahlung>(`${zahlungsPfad(orderId, paymentId)}/documents`, formular, {
    onFortschritt: fortschritt,
  });
}

/** `POST .../payment-actuals/:paymentId/document-links` - `documentId` ist `documents.id`, nicht die Link-id. */
export function belegVerknuepfen(orderId: string, paymentId: string, documentId: string): Promise<IstZahlung> {
  return anfrage<IstZahlung>(`${zahlungsPfad(orderId, paymentId)}/document-links`, {
    methode: "POST",
    koerper: { documentId },
  });
}
